import { forwardRef, useImperativeHandle, useRef, useState } from "react";

// Textarea com menção: ao digitar "@" abre a lista dos envolvidos na conversa e completa "@Nome ".
// Setas ↑/↓ escolhem, Enter ou Tab confirmam, Esc fecha. Quem tem conta no Slack vira menção de verdade lá
// (a API troca "@Nome" por <@U...>); os demais ficam só como texto
const norm = (s) => String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// "@" no começo ou depois de espaço, seguido do que já foi digitado do nome (sem quebra de linha)
const tokenAntesDoCursor = (texto, cursor) => {
  const m = /(^|\s)@([^\s@][^@\n]{0,40})?$/.exec(texto.slice(0, cursor));
  return m ? { inicio: cursor - (m[2]?.length ?? 0) - 1, busca: m[2] ?? "" } : null;
};

const MentionTextarea = forwardRef(({ value, onChange, pessoas, ...props }, ref) => {
  const el = useRef(null);
  const [lista, setLista] = useState(null); // { inicio, itens, ativo }
  useImperativeHandle(ref, () => el.current);

  const atualizar = (texto, cursor) => {
    const tk = tokenAntesDoCursor(texto, cursor);
    if (!tk || pessoas.length === 0) return setLista(null);
    const q = norm(tk.busca).trim();
    // Casa com o começo do nome ou de qualquer sobrenome ("can" acha "Manoel Candido")
    const itens = pessoas.filter((p) => !q || norm(p.nome).split(/\s+/).some((w, i, ws) => ws.slice(i).join(" ").startsWith(q))).slice(0, 8);
    setLista(itens.length ? { inicio: tk.inicio, itens, ativo: 0 } : null);
  };

  const escolher = (p) => {
    const cursor = el.current?.selectionStart ?? value.length;
    const novo = `${value.slice(0, lista.inicio)}@${p.nome} ${value.slice(cursor)}`;
    const pos = lista.inicio + p.nome.length + 2;
    onChange(novo);
    setLista(null);
    requestAnimationFrame(() => { el.current?.focus(); el.current?.setSelectionRange(pos, pos); });
  };

  const onKeyDown = (e) => {
    if (!lista) return props.onKeyDown?.(e);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = lista.itens.length;
      setLista({ ...lista, ativo: (lista.ativo + (e.key === "ArrowDown" ? 1 : n - 1)) % n });
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      escolher(lista.itens[lista.ativo]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setLista(null);
    }
  };

  return (
    <div className="position-relative">
      <textarea
        {...props}
        ref={el}
        value={value}
        onChange={(e) => { onChange(e.target.value); atualizar(e.target.value, e.target.selectionStart); }}
        onClick={(e) => atualizar(value, e.target.selectionStart)}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setLista(null), 150)}
        aria-autocomplete="list"
        aria-expanded={!!lista}
      />
      {lista && (
        <div className="dropdown-menu show shadow" data-testid="mention-list" role="listbox" style={{ top: "100%", left: 0, minWidth: 260, maxHeight: 260, overflowY: "auto" }}>
          <h6 className="dropdown-header">Mencionar na conversa</h6>
          {lista.itens.map((p, i) => (
            <button
              key={p.nome}
              type="button"
              role="option"
              aria-selected={i === lista.ativo}
              data-testid={`mention-option-${i}`}
              className={`dropdown-item d-flex justify-content-between align-items-center ${i === lista.ativo ? "active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); escolher(p); }}
              onMouseEnter={() => setLista({ ...lista, ativo: i })}
            >
              <span>{p.nome} <small className={i === lista.ativo ? "" : "text-muted"}>{p.papel}</small></span>
              {p.slack
                ? <i className="fab fa-slack ml-2" title="Recebe a menção no Slack" />
                : <i className="fas fa-at ml-2 text-muted" title="Só no portal (sem conta vinculada no Slack)" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default MentionTextarea;
